// The fill identity of a form copy: a fill ID made when someone starts filling and a tamper-evident fingerprint made when the copy is
// saved or submitted. This is the TypeScript twin of rd_pdf::fill (Rust); both follow EnterpriseReader/docs/form-fill-fingerprint.md and
// are checked against the same vectors (EnterpriseReader/docs/form-fill-vectors.json), so a copy sealed by the desktop Reader verifies
// here and the other way round.

import { PdfDocument, textString } from "./document";
import { Dict, PName, PRef, Stream } from "./parse";
import { sha256 } from "./crypto";
import { readFields, formData, type RawField } from "./fields";
import { incrementalUpdate, type UpdateCtx } from "./incremental";
import { addAcroFields, addPageAnnots, appendPageContent, readXmp, setInfo, writeXmp } from "./acro";
import { pdfText } from "./form";
import { canonicalPng } from "./png";

export const FIELD_ID = "fg_fill_id";
export const FIELD_FINGERPRINT = "fg_fill_fingerprint";
export const FIELD_TIME = "fg_fill_time";
export const XMP_NS = "http://ns.fowlguard.com/fillid/1.0/";
const VERSION_LINE = "FGFILL1";

export interface FillIdentity {
    fillId: string;
    code: string;
    created: string;
    who: string;
    template: string;
    time: string;
    /** `sha256:<hex>` or `hmac-sha256:<hex>`; empty until sealed. */
    fingerprint: string;
}

export type FillVerdict = { kind: "not-sealed" } | { kind: "match" } | { kind: "changed"; why: string } | { kind: "needs-key" };

const enc = new TextEncoder();
export const hex = (b: Uint8Array): string => Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
export const sha256Hex = (data: Uint8Array | string): string => hex(sha256(typeof data === "string" ? enc.encode(data) : data));

export function hmacSha256(key: Uint8Array, msg: Uint8Array): Uint8Array {
    const k = new Uint8Array(64);
    k.set(key.length > 64 ? sha256(key) : key);
    const inner = new Uint8Array(64 + msg.length);
    for (let i = 0; i < 64; i++) inner[i] = k[i]! ^ 0x36;
    inner.set(msg, 64);
    const ih = sha256(inner);
    const outer = new Uint8Array(64 + 32);
    for (let i = 0; i < 64; i++) outer[i] = k[i]! ^ 0x5c;
    outer.set(ih, 64);
    return sha256(outer);
}

/** The short code of a fill ID: eight characters of Crockford base 32 from its hash, as `FG-XXXX-XXXX`. */
export function codeOf(fillId: string): string {
    const A = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
    const h = sha256(enc.encode(fillId));
    let s = "";
    for (let i = 0; i < 8; i++) s += A[h[i]! & 31];
    return `FG-${s.slice(0, 4)}-${s.slice(4)}`;
}

/** A new fill ID (UUID version 4, lower case) and its code. `random` is for tests. */
export function newFillId(random: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n))): { fillId: string; code: string } {
    const b = random(16);
    b[6] = (b[6]! & 0x0f) | 0x40;
    b[8] = (b[8]! & 0x3f) | 0x80;
    const h = hex(b);
    const id = `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
    return { fillId: id, code: codeOf(id) };
}

/** `2026-10-22T09:15:30Z` */
export function isoNow(d = new Date()): string {
    const p = (n: number, w = 2) => String(n).padStart(w, "0");
    return `${p(d.getUTCFullYear(), 4)}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}Z`;
}

const esc = (s: string) => s.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/\r/g, "\\r");

/** Byte order of the UTF-8 text, the order Rust's String comparison gives. */
export function compareUtf8(a: string, b: string): number {
    const x = enc.encode(a), y = enc.encode(b);
    const n = Math.min(x.length, y.length);
    for (let i = 0; i < n; i++) if (x[i] !== y[i]) return x[i]! - y[i]!;
    return x.length - y.length;
}

/** The structure of the form: a hash of its field names and kinds (the fill's own fields left out). */
export function templateIdOf(fields: Array<{ name: string; kind: string }>): string {
    const lines = fields.filter((f) => !f.name.startsWith("fg_fill_")).map((f) => `${f.name}:${f.kind}`).sort(compareUtf8);
    return `tpl-${sha256Hex(lines.join("\n")).slice(0, 16)}`;
}

/** The text that is hashed: version, template, fill ID, who, when, then every answer by field name. `images` are field name → picture hash. */
export function fillMessage(id: Pick<FillIdentity, "template" | "fillId" | "who" | "time">, rows: Array<[string, string]>, images: Array<[string, string]> = []): string {
    let m = `${VERSION_LINE}\n${id.template}\n${id.fillId}\n${id.who}\n${id.time}\n`;
    const all = [...rows.filter(([k]) => !k.startsWith("fg_fill_")), ...images.map(([name, h]): [string, string] => [`image:${name}`, h])];
    all.sort((a, b) => compareUtf8(a[0], b[0]) || compareUtf8(a[1], b[1]));
    for (const [k, v] of all) m += `${esc(k)}\u001f${esc(v)}\n`;
    return m;
}

export function fingerprintOf(message: string, key?: Uint8Array | null): string {
    const bytes = enc.encode(message);
    return key ? `hmac-sha256:${hex(hmacSha256(key, bytes))}` : `sha256:${hex(sha256(bytes))}`;
}

// ---- reading what a document carries ---------------------------------------------------------------------------------------------

function infoGet(doc: PdfDocument, key: string): string {
    const d = doc.xref.trailer.get("Info");
    return d instanceof Dict ? textString(d.get(key)) : "";
}

const fieldText = (fields: RawField[], name: string): string => fields.find((f) => f.name === name)?.value ?? "";

export function readFillIdentity(doc: PdfDocument, fields = readFields(doc)): FillIdentity | null {
    const fillId = fieldText(fields, FIELD_ID);
    if (!fillId) return null;
    return { fillId, code: codeOf(fillId), created: infoGet(doc, "FGFillCreated"), who: infoGet(doc, "FGFillWho"), template: infoGet(doc, "FGTemplateId"), time: fieldText(fields, FIELD_TIME), fingerprint: fieldText(fields, FIELD_FINGERPRINT) };
}

/** The pixels of the picture in an image field (`/MK /I` → form → first image), straight RGBA. Flate RGB or gray with an optional soft mask. */
export function imageFieldPixels(doc: PdfDocument, f: RawField): { w: number; h: number; rgba: Uint8Array } | null {
    for (const wnum of f.widgets.length ? f.widgets : f.num ? [f.num] : []) {
        const w = doc.xref.fetch(new PRef(wnum, 0));
        if (!(w instanceof Dict)) continue;
        const icon = w.dict("MK")?.get("I");
        if (!(icon instanceof Stream)) continue;
        const xo = icon.dict.dict("Resources")?.dict("XObject");
        const img = xo ? xo.get(xo.keys()[0] ?? "") : null;
        if (!(img instanceof Stream)) continue;
        const iw = img.dict.num("Width"), ih = img.dict.num("Height");
        if (!iw || !ih || img.dict.num("BitsPerComponent", 8) !== 8) continue;
        const cs = img.dict.name("ColorSpace");
        const data = img.bytes();
        const comps = cs === "DeviceGray" ? 1 : cs === "DeviceRGB" ? 3 : 0;
        if (!comps || data.length < iw * ih * comps) continue;
        const sm = img.dict.get("SMask");
        const alpha = sm instanceof Stream && sm.dict.num("Width") === iw && sm.dict.num("Height") === ih ? sm.bytes() : null;
        const rgba = new Uint8Array(iw * ih * 4);
        for (let i = 0; i < iw * ih; i++) {
            rgba[i * 4] = data[i * comps]!;
            rgba[i * 4 + 1] = data[i * comps + (comps === 3 ? 1 : 0)]!;
            rgba[i * 4 + 2] = data[i * comps + (comps === 3 ? 2 : 0)]!;
            rgba[i * 4 + 3] = alpha ? alpha[i] ?? 255 : 255;
        }
        return { w: iw, h: ih, rgba };
    }
    return null;
}

/** An image field has the private /FGImg dictionary on its field or on one of its widgets. */
export function isImageField(doc: PdfDocument, f: RawField): boolean {
    if (f.dict.has("FGImg")) return true;
    return f.widgets.some((n) => { const w = doc.xref.fetch(new PRef(n, 0)); return w instanceof Dict && w.has("FGImg"); });
}

export function imageHashes(doc: PdfDocument, fields: RawField[]): Array<[string, string]> {
    const out: Array<[string, string]> = [];
    for (const f of fields) {
        if (f.kind !== "PushButton" || !isImageField(doc, f)) continue;
        const px = imageFieldPixels(doc, f);
        if (px) out.push([f.name, sha256Hex(canonicalPng(px.w, px.h, px.rgba))]);
    }
    return out;
}

export function verifyFill(doc: PdfDocument, key?: Uint8Array | null): FillVerdict {
    const fields = readFields(doc);
    const id = readFillIdentity(doc, fields);
    if (!id || !id.fingerprint) return { kind: "not-sealed" };
    if (templateIdOf(fields) !== id.template) return { kind: "changed", why: "fields were added, removed or renamed since the fill started" };
    const msg = fillMessage(id, formData(fields), imageHashes(doc, fields));
    const keyed = id.fingerprint.startsWith("hmac-sha256:");
    if (keyed && !key) return { kind: "needs-key" };
    return fingerprintOf(msg, keyed ? key : null) === id.fingerprint ? { kind: "match" } : { kind: "changed", why: "an answer, a picture, the fill ID or the time is not what was sealed" };
}

/** The fill-identity field values as `[name, value]`, the way a submission carries them. */
export function fillPayload(doc: PdfDocument): Array<[string, string]> {
    const fields = readFields(doc);
    return [FIELD_ID, FIELD_FINGERPRINT, FIELD_TIME].flatMap((n) => { const f = fields.find((x) => x.name === n); return f ? [[n, f.value] as [string, string]] : []; });
}

// ---- writing ---------------------------------------------------------------------------------------------------------------------

const xml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const BEGIN = "<!--fg-fill-begin-->";
const END = "<!--fg-fill-end-->";

export function xmpBlock(id: FillIdentity): string {
    return `${BEGIN}<rdf:Description rdf:about="" xmlns:fgfill="${XMP_NS}"><fgfill:FillID>${xml(id.fillId)}</fgfill:FillID><fgfill:FillCode>${xml(id.code)}</fgfill:FillCode><fgfill:Created>${xml(id.created)}</fgfill:Created><fgfill:Who>${xml(id.who)}</fgfill:Who><fgfill:Template>${xml(id.template)}</fgfill:Template><fgfill:Time>${xml(id.time)}</fgfill:Time><fgfill:Fingerprint>${xml(id.fingerprint)}</fgfill:Fingerprint></rdf:Description>${END}`;
}

export function mergeXmp(existing: string, id: FillIdentity): string {
    let x = existing || `<?xpacket begin="﻿" id="W5M0MpCehiHzreSzNTczkc9d"?><x:xmpmeta xmlns:x="adobe:ns:meta/"><rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#"></rdf:RDF></x:xmpmeta><?xpacket end="w"?>`;
    const a = x.indexOf(BEGIN), b = x.indexOf(END);
    if (a >= 0 && b > a) x = x.slice(0, a) + x.slice(b + END.length);
    const i = x.indexOf("</rdf:RDF>");
    const block = xmpBlock(id);
    return i >= 0 ? x.slice(0, i) + block + x.slice(i) : x + block;
}

const pn = (s: string) => new PName(s);

/** Writes (or rewrites) a hidden, read-only text field `name` holding `value`. */
function hiddenTextField(c: UpdateCtx, existing: RawField[], name: string, value: string, created: PRef[]): void {
    const f = existing.find((x) => x.name === name);
    if (f?.num) {
        const d = c.copy(f.num);
        d.set("V", pdfText(value));
        d.set("Ff", (d.num("Ff") | 1) >>> 0);
        c.put(f.num, d);
        return;
    }
    const n = c.newRef();
    const pref = c.pageRef(0);
    const d = new Dict(null);
    d.set("Type", pn("Annot")); d.set("Subtype", pn("Widget")); d.set("FT", pn("Tx"));
    d.set("T", pdfText(name)); d.set("V", pdfText(value)); d.set("Ff", 1); d.set("F", 2);
    d.set("Rect", [0, 0, 1, 1]);
    if (pref) d.set("P", pref);
    c.put(n, d);
    created.push(new PRef(n, 0));
}

export interface FillResult { bytes: Uint8Array; identity: FillIdentity }

async function writeIdentity(c: UpdateCtx, id: FillIdentity, fields: RawField[], infoPairs: Array<[string, string]>): Promise<void> {
    const created: PRef[] = [];
    hiddenTextField(c, fields, FIELD_ID, id.fillId, created);
    hiddenTextField(c, fields, FIELD_FINGERPRINT, id.fingerprint, created);
    hiddenTextField(c, fields, FIELD_TIME, id.time, created);
    if (created.length) { addPageAnnots(c, 0, created); addAcroFields(c, created); }
    setInfo(c, infoPairs);
    await writeXmp(c, mergeXmp(readXmp(c), id));
}

/** Starts a fill: a new fill ID, the form's structure noted. A copy that already has an identity is returned unchanged unless `force`. */
export async function startFill(original: Uint8Array, who: string, opts: { force?: boolean; random?: (n: number) => Uint8Array; now?: Date; /** A fill ID chosen earlier (when the first answer was typed). */ fillId?: string } = {}): Promise<FillResult> {
    const doc0 = PdfDocument.open(original);
    const fields0 = readFields(doc0);
    const have = readFillIdentity(doc0, fields0);
    if (have && !opts.force) return { bytes: original, identity: have };
    const { fillId, code } = opts.fillId ? { fillId: opts.fillId, code: codeOf(opts.fillId) } : newFillId(opts.random);
    const id: FillIdentity = { fillId, code, created: isoNow(opts.now), who, template: templateIdOf(fields0), time: "", fingerprint: "" };
    const bytes = await incrementalUpdate(original, (c) => writeIdentity(c, id, fields0, [["FGFillID", fillId], ["FGFillCode", code], ["FGFillCreated", id.created], ["FGFillWho", who], ["FGTemplateId", id.template], ["FGFillFingerprint", ""]]));
    return { bytes, identity: id };
}

/** Makes the fingerprint now (a save or a submit) and writes it where other tools read it. `key` signs it with an HMAC. Null when the copy has no fill identity. */
export async function sealFill(original: Uint8Array, key?: Uint8Array | null, now?: Date): Promise<FillResult | null> {
    const doc0 = PdfDocument.open(original);
    const fields0 = readFields(doc0);
    const id0 = readFillIdentity(doc0, fields0);
    if (!id0) return null;
    const id: FillIdentity = { ...id0, time: isoNow(now) };
    if (!id.template) id.template = templateIdOf(fields0);
    id.fingerprint = fingerprintOf(fillMessage(id, formData(fields0), imageHashes(doc0, fields0)), key);
    const bytes = await incrementalUpdate(original, (c) => writeIdentity(c, id, fields0, [["FGFillFingerprint", id.fingerprint], ["FGFillTime", id.time], ...(id0.template ? [] : [["FGTemplateId", id.template] as [string, string]])]));
    return { bytes, identity: id };
}

// ---- the optional bar code stamp ---------------------------------------------------------------------------------------------------

const P128 = ["212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213", "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132", "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211", "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313", "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331", "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111", "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214", "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111", "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141", "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141", "114131", "311141", "411131", "211412", "211214", "211232", "2331112"];

/** Code 128 (set B) module widths, bar first: start, the characters, the check digit, stop. */
export function code128Widths(text: string): number[] {
    const codes = [104];
    for (const ch of text) codes.push(Math.min(126, Math.max(32, ch.codePointAt(0)!)) - 32);
    const sum = codes.reduce((s, c, i) => s + (i === 0 ? c : c * i), 0);
    codes.push(sum % 103, 106);
    return codes.flatMap((c) => [...P128[c]!].map(Number));
}

/** Draws the fill code as a Code 128 bar code with its words below, in `rect` (x0, y0, x1, y1) of page `pageIndex`. */
export async function stampFill(original: Uint8Array, pageIndex: number, rect: [number, number, number, number]): Promise<Uint8Array | null> {
    const doc0 = PdfDocument.open(original);
    const id = readFillIdentity(doc0);
    if (!id) return null;
    const [x0, y0, x1, y1] = rect;
    const widths = code128Widths(id.code);
    const total = widths.reduce((s, w) => s + w, 0) + 20;
    const unit = Math.min(2, (x1 - x0) / total);
    const barH = Math.max(8, y1 - y0 - 11);
    let x = x0 + ((x1 - x0) - total * unit) / 2 + 10 * unit;
    const n3 = (v: number) => v.toFixed(3);
    let c = "q 0 g\n";
    widths.forEach((w, i) => { const wv = w * unit; if (i % 2 === 0) c += `${n3(x)} ${n3(y0 + 11)} ${n3(wv)} ${n3(barH)} re f\n`; x += wv; });
    c += "Q\n";
    const tw = id.code.length * 0.6 * 8; // Courier: 600 units per character
    c += `BT /FgStamp 8 Tf 0 g ${n3(x0 + ((x1 - x0) - tw) / 2)} ${n3(y0 + 2)} Td (${id.code}) Tj ET\n`;
    return incrementalUpdate(original, (ctx) => appendPageContent(ctx, pageIndex, c, { Font: { FgStamp: "<< /Type /Font /Subtype /Type1 /BaseFont /Courier /Encoding /WinAnsiEncoding >>" } }));
}
